// @flow

import * as React from 'react';
import { useMutation } from 'urql';
import fastDeepEqual from 'fast-deep-equal';

import LogInteractions, {
  type InteractionV4Input,
  type InteractionWithTimestampInput,
} from './LogInteractions.gql';

const INTERACTION_BUFFER_KEY = 'interaction_mutation_buffer';

const removeFromBuffer = events => {
  try {
    const buffer = JSON.parse(localStorage.getItem(INTERACTION_BUFFER_KEY) || '[]');
    localStorage.setItem(
      INTERACTION_BUFFER_KEY,
      JSON.stringify(buffer.filter(item => events.every(event => !fastDeepEqual(item, event)))),
    );
  } catch (e) {
    // Noop: There's nothing we can do if localstorage throws.
  }
};

const addToBuffer = event => {
  try {
    const buffer = JSON.parse(localStorage.getItem(INTERACTION_BUFFER_KEY) || '[]');
    buffer.push(event);
    localStorage.setItem(INTERACTION_BUFFER_KEY, JSON.stringify(buffer));
  } catch (e) {
    // Noop: There's nothing we can do if localstorage throws.
  }
};

const readFromBuffer = () => {
  try {
    return JSON.parse(localStorage.getItem(INTERACTION_BUFFER_KEY) || '[]');
  } catch (e) {
    // Noop: There's nothing we can do if localstorage throws.
  }
  return [];
};

let locked = false;
const attemptToLog = executeMutation => {
  const bufferedEvents = ((readFromBuffer(): any): InteractionWithTimestampInput[]);
  if (bufferedEvents.length > 0 && !locked) {
    locked = true;
    executeMutation({
      interactions: bufferedEvents,
      clientRequestTimestamp: Date.now(),
    })
      .then(() => {
        removeFromBuffer(bufferedEvents);
      })
      .finally(() => {
        locked = false;
      });
  } else if (bufferedEvents.length > 0 && locked) {
    setTimeout(() => attemptToLog(executeMutation), 1000);
  }
};

const useInteractionLogger = () => {
  const [, executeMutation] = useMutation(LogInteractions);
  return React.useCallback(
    (interaction: InteractionV4Input) => {
      // eslint-disable-next-line ta-code-conventions/lithium-string-blacklist
      if (SERVER) {
        throw new Error(
          'Attempting to log an interaction on Server Side Render: this is impossible.',
        );
      }
      const event = { interaction, eventTimestampMsClient: Date.now() };
      addToBuffer(event);
      // We push this log call out beyond the current tick to ensure we're not following a link.
      setTimeout(() => attemptToLog(executeMutation), 1000);
    },
    [executeMutation],
  );
};

const useSubmitInteractionBufferEffect = () => {
  const [, executeMutation] = useMutation(LogInteractions);
  React.useEffect(() => {
    requestIdleCallback(() => {
      attemptToLog(executeMutation);
    });
  }, [executeMutation]);
};

export { useInteractionLogger, useSubmitInteractionBufferEffect };
